import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../../hooks/usePageMeta';
import { PageHero } from '../../components/ui/PageHero';
import { Section } from '../../components/ui/Section';
import { ActionLink } from '../../components/ui/ActionLink';
import { TextLink } from '../../components/ui/TextLink';
import { BenefitList } from '../../components/ui/BenefitList';
import { MockupTag } from '../../components/ui/MockupTag';
import { EnquirySection } from '../../components/forms/EnquirySection';
import { destinations } from '../../data/destinations';
import { courses } from '../../data/courses';
import { choosingTips, sameEverywhere } from '../../data/pageContent';
import { images } from '../../data/images';
import { destinationPath } from '../../utils/paths';
import type { Destination, Season } from '../../types/content';
export function DestinationsIndex() {
  usePageMeta('Study English Abroad | Year-round and Seasonal Locations | ES World', 'Study English abroad with ES World. Choose a year-round campus or a seasonal programme. See courses and ask for prices.');
  const yearRound = destinations.filter(d => d.yearRound);
  const seasonal = destinations.filter(d => d.seasonal);
  return <>
      <PageHero breadcrumbs={[{
      label: 'Destinations',
      to: '/destinations'
    }]} title="Study English abroad, all year or by season" lead="Study English abroad at an ES World campus. Our year-round campuses run courses every week of the year. Our seasonal locations host summer and winter programmes. Every location uses the same teaching method, levels and student support." primary={{
      label: 'Help me choose a location',
      to: '#enquire'
    }} secondary={{
      label: 'View seasonal locations',
      to: '#seasonal'
    }} image={images.social} imageAlt="Students on a city walking tour" data-id="element-1078" data-mp="14jymfenqwahd" />

      <Section id="year-round" title="Study with us all year" description="Our year-round campuses run English, exam and professional courses every week. You can start on any Monday." data-id="element-1079" data-mp="1sc5l4kgy0tjy">
        <DestinationGrid items={yearRound} season="year-round" data-id="element-1080" data-mp="24eyjhqfbf44t" />
        <div className="mt-12" data-id="element-1081" data-mp="29czxvy1sqf9y">
          <ActionLink to="/programmes" data-id="element-1082" data-mp="29byody0fi0uc">Compare programmes first</ActionLink>
        </div>
      </Section>

      <Section id="seasonal" bg="cream" title="Join a summer or winter programme" description="Seasonal locations host camps, mini-stays and short courses on set dates. Young learners are cared for by trained group leaders." data-id="element-1083" data-mp="2e4rqsf8fcx39">
        <DestinationGrid items={seasonal} season="seasonal" data-id="element-1084" data-mp="2s3r78zlsa5oc" />
      </Section>

      <Section id="same" title="The same quality in every location" description="ES World is one school. Wherever you study, you join the same kind of class and receive the same kind of support." data-id="element-1085" data-mp="2s1ojync0mqnb">
        <BenefitList items={sameEverywhere} columns={3} data-id="element-1086" data-mp="xrt7do9d6m54" />
      </Section>

      <Section id="choose" bg="grey" title="Choose your location in minutes" description="Four simple questions help you decide where to study." aside={<TextLink to="/contact" data-id="element-1088" data-mp="qdk082eiod5b">Ask our team about visas</TextLink>} data-id="element-1087" data-mp="12p33fmr9dw1x">
        <BenefitList items={choosingTips} data-id="element-1089" data-mp="lefr1brlefez" />
      </Section>

      <EnquirySection title="Ask us where to study" description="Tell us your programme, your dates and your nationality. We suggest the locations that fit your plans and explain the visa steps." submitLabel="Send my location question" data-id="element-1090" data-mp="lcw8tfmdluak" />
    </>;
}
function DestinationGrid({
  items,
  season
}: {
  items: Destination[];
  season: Season;
}) {
  return <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1091" data-mp="143vtw5qr2jyk">
      {items.map(destination => {
      const count = courses.filter(course => course.season === season && course.locations.includes(destination.slug)).length;
      return <li key={`${season}-${destination.slug}`} data-id="element-1092" data-mp="3jwcx710t8zyz">
            <Link to={destinationPath(destination, season)} className="group block" data-id="element-1093" data-mp="3jyz551rm5hhq">
              <div className="relative overflow-hidden rounded-2xl" data-id="element-1094" data-mp="1ck3ah0l1jpwt">
                <img src={destination.image} alt={`View of ${destination.name}`} className="aspect-[4/3] w-full object-cover transition-transform duration-300 ease-brand group-hover:scale-[1.02]" data-id="element-1095" data-mp="1hcmo1xjqal1m" />
                <MockupTag note="Placeholder image" className="absolute left-3 top-3" data-id="element-1096" data-mp="1vc5qqatrt46j" />
              </div>
              <div className="mt-4 flex items-baseline justify-between gap-4" data-id="element-1097" data-mp="20ayu3afcfesz">
                <h3 className="type-card-title text-ink transition-colors duration-150 group-hover:text-brand-press" data-id="element-1098" data-mp="208w6sy5krzry">
                  {destination.name}
                </h3>
                <span className="type-small text-muted" data-id="element-1099" data-mp="1qb80rc2ir5uh">{count} programmes</span>
              </div>
              <p className="mt-1 type-card-desc" data-id="element-1100" data-mp="9xhvi1znybj9">{destination.country}</p>
            </Link>
          </li>;
    })}
    </ul>;
}